import React, { forwardRef, useImperativeHandle, useMemo, useRef } from 'react'
import { Field, Form } from 'react-final-form'
import { FormattedMessage, useIntl } from 'react-intl'

import { format, isAfter, isValid, parse, startOfDay, subYears } from 'date-fns'
import { FormApi } from 'final-form'
import styled, { useTheme } from 'styled-components/native'

import { sanitizeEmailAddressInput } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/stringUtils'
import { isStarbucksUser } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/userUtils'
import { isValidEmailString } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/validationUtils'
import { CustomerInfo } from '@lyrahealth-inc/shared-app-logic/src/models/customer/Customer'

import { BodyText, Size as BodyTextSize } from '../../atoms/bodyText/BodyText'
import { FormContainer } from '../../atoms/formElements/formUtils'
import { InputFieldRFF } from '../../atoms/formElements/InputField'
import { MaskedInputRFF } from '../../atoms/formElements/MaskedInput'
import { SelectFieldRFF } from '../../atoms/formElements/SelectField'
import { TypeAheadRFF } from '../../atoms/formElements/typeAhead/TypeAhead'
import { TypeAheadCustomOptionsConfig } from '../../atoms/formElements/typeAhead/TypeAheadCustomOptionsConfig'
import { CalendarIcon } from '../../atoms/icons/CalendarIcon'
import { SearchIcon } from '../../atoms/icons/SearchIcon'
import { ThemeType } from '../../utils/themes/ThemeProvider'

const Container = styled.View(({ theme }) => ({
  flexDirection: 'column',
  gap: theme.spacing['16px'],
  width: '100%',
}))

const Row = styled.View(({ theme }) => ({
  flexDirection: 'row',
  gap: theme.spacing['16px'],
}))

const IconContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  marginRight: theme.spacing['8px'],
}))

export type AddNewClientFormValues = {
  company: string
  firstName: string
  lastName: string
  dateOfBirth: string
  careType: string
  email: string
  pid?: string
}

export type AddNewClientContentProps = {
  customers: CustomerInfo[]
  careTypeOptions: Array<{ label: string; value: string }>
  isFetchingCareTypes?: boolean
  onCompanyChange?: (company: string) => void
  selectedCompany?: string | null
  initialValues?: Partial<AddNewClientFormValues>
  showEmailField?: boolean
  isTransitioning?: boolean
  isRegistered?: boolean
}

export type AddNewClientContentRef = {
  submit: () => Promise<{
    company: string
    firstName: string
    lastName: string
    dateOfBirth: string
    careType: string
    email: string
    pid?: string
  } | null>
  reset: () => void
}

export const AddNewClientContent = forwardRef<AddNewClientContentRef, AddNewClientContentProps>(
  (
    {
      customers,
      careTypeOptions,
      isFetchingCareTypes,
      onCompanyChange,
      selectedCompany,
      initialValues,
      showEmailField = true,
      isTransitioning,
      isRegistered,
    },
    ref,
  ) => {
    const { formatMessage } = useIntl()
    const submitForm = useRef<() => Promise<AddNewClientFormValues | undefined> | undefined>()
    const AddNewClientForm = useRef<FormApi<AddNewClientFormValues> | null>(null)

    const { colors } = useTheme()

    useImperativeHandle(
      ref,
      () => ({
        submit: async () => {
          await submitForm.current?.()
          const formState = AddNewClientForm.current?.getState()
          if (!formState || formState.invalid || formState.submitting) return null

          const { company, firstName, lastName, dateOfBirth, careType, email, pid } = formState.values
          const parsedDob = parse(dateOfBirth, 'MM/dd/yyyy', new Date())

          return {
            company,
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            dateOfBirth: format(parsedDob, 'yyyy-MM-dd'),
            careType,
            email: sanitizeEmailAddressInput(email) ?? '',
            pid: pid?.trim(),
          }
        },
        reset: () => {
          AddNewClientForm.current?.reset()
        },
      }),
      [],
    )

    const mergedInitialValues: AddNewClientFormValues = useMemo(
      () => ({
        company: '',
        firstName: '',
        lastName: '',
        dateOfBirth: '',
        careType: '',
        email: '',
        pid: undefined,
        ...initialValues,
      }),
      [initialValues],
    )

    const validate = (values: AddNewClientFormValues) => {
      const errors: Partial<Record<keyof AddNewClientFormValues, string | React.ReactNode>> = {}
      if (!values.company) {
        errors.company = formatMessage({
          defaultMessage: 'Sponsoring company is required',
          description: 'Error message for when provider does not select a sponsoring company',
        })
      }
      if (!values.firstName || values.firstName.trim() === '') {
        errors.firstName = formatMessage({
          defaultMessage: 'Legal first name is required',
          description: 'Error message for when provider does not input a legal first name',
        })
      }
      if (!values.lastName || values.lastName.trim() === '') {
        errors.lastName = formatMessage({
          defaultMessage: 'Legal last name is required',
          description: 'Error message for when provider does not input a legal last name',
        })
      }
      if (!values.dateOfBirth) {
        errors.dateOfBirth = formatMessage({
          defaultMessage: 'Date is required',
          description: 'Error message for when provider does not properly input a date',
        })
      }
      if (!errors.dateOfBirth) {
        const parsed = parse(values.dateOfBirth, 'MM/dd/yyyy', new Date())
        const today = startOfDay(new Date())

        if (!isValid(parsed)) {
          errors.dateOfBirth = formatMessage({
            defaultMessage: 'Invalid date',
            description: 'Error message for when provider enters an invalid date',
          })
        } else if (isAfter(startOfDay(parsed), subYears(today, 18))) {
          errors.dateOfBirth = (
            <FormattedMessage
              defaultMessage='Registration links unavailable for minors.{newline}Instruct the guardian or client to contact our Care Navigator Team at <bold>care@lyrahealth.com</bold> to initiate scheduling.'
              description='Error message for when provider tries to add a minor as a new client'
              values={{
                newline: '\n',
                bold: (chunks) => (
                  <BodyText
                    bold
                    size={BodyTextSize.SMALL}
                    color={colors.textError}
                    text={chunks as unknown as string}
                  />
                ),
              }}
            />
          )
        }
      }
      if (!values.careType && values.company) {
        errors.careType = formatMessage({
          defaultMessage: 'Care type is required',
          description: 'Error message for when provider does not select a care type',
        })
      }
      if (showEmailField && (!values.email || values.email.trim() === '')) {
        errors.email = formatMessage({
          defaultMessage: 'Email is required',
          description: 'Error message for when provider does not enter an email address',
        })
      }
      if (!isRegistered && isStarbucksUser(selectedCompany) && !values.pid?.trim()) {
        errors.pid = formatMessage({
          defaultMessage: 'Partner ID is required',
          description: 'Error message for when provider does not enter a PID for a Starbucks client',
        })
      }
      if (showEmailField && !errors.email) {
        const sanitizedEmail = sanitizeEmailAddressInput(values.email)
        if (sanitizedEmail && !isValidEmailString(sanitizedEmail)) {
          errors.email = formatMessage({
            defaultMessage: 'Invalid email',
            description: 'Error message for when provider enters an invalid email address',
          })
        }
      }
      return errors
    }

    return (
      <Form
        initialValues={mergedInitialValues}
        onSubmit={() => {}}
        validate={validate}
        render={({ handleSubmit, form }) => {
          submitForm.current = handleSubmit as () => Promise<AddNewClientFormValues | undefined>
          AddNewClientForm.current = form
          return (
            <FormContainer handleSubmit={handleSubmit}>
              <Container>
                <Field
                  name='company'
                  component={TypeAheadRFF}
                  label={formatMessage({
                    defaultMessage: 'Sponsoring Company',
                    description: 'Label for search field to select a sponsoring company of the new client',
                  })}
                  forceMatch
                  readOnly={isTransitioning}
                  customOptionsConfig={
                    new TypeAheadCustomOptionsConfig(customers, undefined, undefined, 'label', 'value')
                  }
                  multiSelect={false}
                  leftIcon={
                    <IconContainer>
                      <SearchIcon fillColor={colors.iconDefault} />
                    </IconContainer>
                  }
                  baseInputStyle={{ marginBottom: 0 }}
                  onValueChange={(value: string | string[]) => {
                    const companyValue = Array.isArray(value) ? value[0] : value
                    form.change('careType', '')
                    form.change('pid', undefined)
                    onCompanyChange?.(companyValue ?? '')
                  }}
                />
                <Row>
                  <Field
                    name='firstName'
                    type='text'
                    component={InputFieldRFF}
                    readOnly={isTransitioning}
                    label={formatMessage({
                      defaultMessage: 'Legal First Name',
                      description: 'Label for a text field for first name of the new client',
                    })}
                    baseInputStyle={{ marginBottom: 0, flex: 1 }}
                  />
                  <Field
                    name='lastName'
                    type='text'
                    component={InputFieldRFF}
                    readOnly={isTransitioning}
                    label={formatMessage({
                      defaultMessage: 'Legal Last Name',
                      description: 'Label for a text field for the last name of the new client',
                    })}
                    baseInputStyle={{ marginBottom: 0, flex: 1 }}
                  />
                </Row>
                <Row>
                  <Field
                    name='dateOfBirth'
                    label={formatMessage({
                      defaultMessage: 'Date of birth',
                      description: 'Label of field for the date of birth of the new client',
                    })}
                    type='tel'
                    placeholder='MM/DD/YYYY'
                    mask='11/11/1111'
                    component={MaskedInputRFF}
                    readOnly={isTransitioning}
                    baseInputStyle={{ marginBottom: 0, flex: 1 }}
                    rightIcon={
                      <IconContainer>
                        <CalendarIcon fillColor={colors.iconDefault} />
                      </IconContainer>
                    }
                  />
                  <Field
                    name='careType'
                    component={SelectFieldRFF}
                    label={formatMessage({
                      defaultMessage: 'Care type',
                      description: 'Label for dropdown to select the care type for the new client',
                    })}
                    options={careTypeOptions}
                    disabled={!selectedCompany || isFetchingCareTypes}
                    baseInputStyle={{ marginBottom: 0, flex: 1 }}
                  />
                </Row>
                {showEmailField && (
                  <Field
                    name='email'
                    type='text'
                    component={InputFieldRFF}
                    readOnly={isTransitioning && !!mergedInitialValues.email}
                    label={formatMessage({
                      defaultMessage: 'Email',
                      description: 'Label for a text field for client email',
                    })}
                    baseInputStyle={{ marginBottom: 0 }}
                  />
                )}
                {!isRegistered && isStarbucksUser(selectedCompany) && (
                  <Field
                    name='pid'
                    type='text'
                    component={InputFieldRFF}
                    label={formatMessage({
                      defaultMessage: 'Partner ID (PID)',
                      description: 'Label for field for Starbucks partner ID',
                    })}
                    toolTipContent={formatMessage({
                      defaultMessage: `If your client doesn't have their Partner ID on hand, they can get it from their HR/Benefits team. This will be needed to generate their Lyra Code for billing purposes.`,
                      description: 'Tooltip text explaining how to find the partner ID of the input patient',
                    })}
                    baseInputStyle={{ marginBottom: 0 }}
                    toolTipHoverEnabled
                  />
                )}
              </Container>
            </FormContainer>
          )
        }}
      />
    )
  },
)
